import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Play, Plus, Check, Lock, Film, Tv, MousePointerClick, ChevronDown } from "lucide-react";
import Wordmark from "../components/Wordmark";
import api from "../lib/api";
import { SKIN_TEASERS, FREE_SKINS, getLayout } from "../lib/theme";

const HEADLINE = "Every service you pay for. One dial to find it all.";

const SAMPLE = [
  { t: "Neon Harbor", m: "Sci-Fi · 2024" },
  { t: "The Lantern Keeper", m: "Drama · 2024" },
  { t: "Midnight Atlas", m: "Thriller · 2023" },
  { t: "Paper Cities", m: "Indie · 2025" },
  { t: "Velvet Static", m: "Mystery · 2022" },
  { t: "Concrete Tides", m: "Romance · 2023" },
  { t: "Afterglow", m: "Drama · 2025" },
  { t: "Dust & Gold", m: "Western · 2021" },
];

const ERA_FLAVOR = {
  "classic-red": "Cinemascope dark with blood-red billboard energy.",
  "early2010s": "Bright, boxy and busy — the catalog-portal era.",
  "glassshelf": "Frosted glass and softly floating reflections.",
  "thegrid": "The channel-guide grid, all business.",
  "videostore": "Shelves of boxes with hand-written rental tags.",
  "spotlightblue": "Colossal hero art with flagship-store gloss.",
  "neon80s": "Scanlines, chrome and neon marquee glow.",
};

const SERVICE_NAMES = ["Netflix", "Hulu", "Apple TV+", "Disney+", "Max", "Prime Video", "Paramount+", "Peacock", "YouTube TV"];

const FAQS = [
  { q: "Is The Dial free?", a: "Yes — two era skins (Classic and Early 2010s) are free forever. Full Access unlocks all eight eras." },
  { q: "Do I watch movies on The Dial?", a: "No. You browse and discover here, then tap through and press play in the service's own app. The Dial is the guide, not the channel." },
  { q: "Which services does it cover?", a: "The major streaming subscriptions: " + SERVICE_NAMES.join(", ") + "." },
  { q: "What are era skins?", a: "The whole interface restyles into different TV eras — same features, a completely different vibe. Pick the one that feels like home." },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---- Poster tile: real TMDB poster when available, else palette-driven fallback ----
function Poster({ i, title, meta, poster, badges, shape }) {
  const grads = [
    "linear-gradient(150deg, var(--accent) 0%, var(--bg-card) 55%, var(--bg-base) 100%)",
    "linear-gradient(150deg, var(--bg-surface) 0%, var(--bg-card) 60%, var(--accent) 140%)",
    "linear-gradient(150deg, var(--bg-card) 0%, var(--bg-base) 70%)",
    "linear-gradient(150deg, var(--accent) -20%, var(--bg-base) 60%)",
  ];
  const base = "relative overflow-hidden bg-[var(--bg-card)] border border-[var(--border-subtle)]";
  const shapeCls = shape === "box" ? "rounded-none border-2" : shape === "cell" ? "rounded-none" : "rounded-[var(--card-radius)]";
  return (
    <div className={`${base} ${shapeCls} aspect-[2/3] w-full`} data-testid="demo-poster">
      {poster ? (
        <img src={poster} alt={title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <>
          <div className="absolute inset-0" style={{ backgroundImage: grads[i % grads.length] }} />
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(var(--text-primary) 0.5px, transparent 0.5px)", backgroundSize: "6px 6px" }} />
        </>
      )}
      {badges && badges.length > 0 && (
        <div className="absolute bottom-1 right-1 z-10 flex items-center gap-0.5 rounded bg-black/70 p-0.5 backdrop-blur-sm" data-testid="demo-badges">
          {badges.slice(0, 3).map((b, k) => (
            b.logo
              ? <span key={k} className="flex h-4 w-4 items-center justify-center overflow-hidden rounded-[3px] bg-white" title={b.name}><img src={`${BACKEND}${b.logo}`} alt={b.name} loading="lazy" className="h-full w-full object-contain" /></span>
              : <span key={k} className="flex h-4 items-center rounded-[3px] bg-white px-0.5 text-[6px] font-bold uppercase leading-none text-black" title={b.name}>{b.name.slice(0, 4)}</span>
          ))}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 pr-10">
        <p className="font-head text-[11px] leading-tight text-white drop-shadow line-clamp-1">{title}</p>
        {meta && <p className="text-[8px] uppercase tracking-wider text-white/70 line-clamp-1">{meta}</p>}
      </div>
    </div>
  );
}

function ShimmerTile({ shape }) {
  const shapeCls = shape === "box" ? "rounded-none border-2" : shape === "cell" ? "rounded-none" : "rounded-[var(--card-radius)]";
  return <div className={`aspect-[2/3] w-full animate-pulse border border-[var(--border-subtle)] bg-[var(--bg-card)] ${shapeCls}`} data-testid="demo-shimmer" />;
}

// ---- Playable mock browse UI, restyled by the active demo skin ----
function MockBrowse({ skinId, items, loading }) {
  const L = getLayout(skinId);
  const feature = items[0] || {};
  const navBorder = {
    utility: "border-b-2 border-[var(--accent)]",
    guide: "border-t-2 border-[var(--accent)]",
    scanline: "border-b-2 border-[var(--border-accent)]",
  }[L.nav] || "border-b border-[var(--border-subtle)]";

  return (
    <div data-skin={skinId} data-testid="demo-mock" className="overflow-hidden rounded-[var(--card-radius)] border border-[var(--border-subtle)] shadow-2xl">
      <div className="bg-[var(--bg-base)]">
        {/* nav */}
        <div className={`flex items-center gap-3 px-4 py-3 ${navBorder} ${L.nav === "float" ? "justify-center" : ""}`}>
          <span className="font-head text-sm tracking-widest">
            <span className={L.nav === "utility" ? "rounded-sm bg-[var(--accent)] px-1 text-[var(--accent-contrast)]" : "text-[var(--accent)]"}>THE </span>
            <span className={L.nav === "utility" ? "" : "text-[var(--text-primary)]"} style={L.nav === "utility" ? { color: "var(--text-primary)" } : undefined}>DIAL</span>
          </span>
          {L.nav !== "float" && (
            <>
              <span className="hidden text-[10px] uppercase tracking-wider text-[var(--text-secondary)] sm:inline">Home</span>
              <span className="hidden text-[10px] uppercase tracking-wider text-[var(--text-secondary)] sm:inline">My List</span>
              <div className="ml-auto flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-card)] px-2 py-1">
                <Search size={11} className="text-[var(--text-muted)]" />
                <span className="text-[9px] text-[var(--text-muted)]">Search titles…</span>
              </div>
            </>
          )}
        </div>

        {/* hero (skipped for the grid layout) */}
        {L.home !== "guide" && (
          <div className="relative m-4 mb-3 overflow-hidden rounded-[var(--card-radius)]" style={{ height: L.hero === "colossal" ? 150 : L.hero === "marquee" ? 120 : 130 }}>
            {feature.poster
              ? <img src={feature.poster} alt={feature.title} className="absolute inset-0 h-full w-full object-cover" />
              : <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(120deg, var(--accent) -10%, var(--bg-card) 45%, var(--bg-base) 100%)" }} />}
            {L.nav === "scanline" && <div className="absolute inset-0 scanlines opacity-40" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] via-transparent to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              <p className="font-head text-lg leading-none text-white drop-shadow sm:text-2xl line-clamp-1">{feature.title || "Featured"}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-white/70">Featured · Trending now</p>
              <div className="mt-2 flex gap-2">
                <span className="inline-flex items-center gap-1 rounded-[var(--card-radius)] bg-[var(--accent)] px-3 py-1 text-[10px] font-bold uppercase text-[var(--accent-contrast)]"><Play size={10} /> Where to watch</span>
                <span className="inline-flex items-center gap-1 rounded-[var(--card-radius)] border border-white/40 px-3 py-1 text-[10px] font-bold uppercase text-white"><Plus size={10} /> My List</span>
              </div>
            </div>
          </div>
        )}

        {/* rows */}
        <div className="px-4 pb-4">
          {L.home === "guide" ? (
            <div className="grid grid-cols-4 gap-1 sm:grid-cols-6" data-testid="demo-grid">
              {items.concat(items.slice(0, 4)).slice(0, 12).map((s, i) => (
                <div key={i} className={`flex h-10 items-center justify-center border border-[var(--border-subtle)] p-0.5 text-center text-[8px] leading-tight line-clamp-2 ${i === 3 ? "bg-[var(--accent)] text-[var(--accent-contrast)]" : "bg-[var(--bg-card)] text-[var(--text-secondary)]"}`}>{s.title}</div>
              ))}
            </div>
          ) : (
            <>
              <p className="mb-2 font-head text-xs uppercase tracking-wider text-[var(--text-secondary)]">Trending across your services</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => <ShimmerTile key={i} shape={L.card} />)
                  : items.slice(0, 6).map((s, i) => <Poster key={i} i={i} title={s.title} meta={s.meta} poster={s.poster} badges={s.badges} shape={L.card} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ children, className = "", id }) {
  return <section id={id} className={`mx-auto max-w-6xl px-5 sm:px-8 ${className}`}>{children}</section>;
}

const FALLBACK = SAMPLE.map((s) => ({ title: s.t, meta: s.m, poster: null }));

export default function Landing() {
  const [demoSkin, setDemoSkin] = useState("classic-red");
  const [openFaq, setOpenFaq] = useState(0);
  const [items, setItems] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);
  const demoRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/demo/trending")
      .then(({ data }) => { if (alive && data?.items?.length) setItems(data.items); })
      .catch(() => { /* keep hardcoded fallback so demo never looks broken */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const goDemo = (id) => {
    if (id) setDemoSkin(id);
    demoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const CtaPrimary = ({ testid }) => (
    <Link to="/register" data-testid={testid}
      className="inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-7 py-3.5 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)]">
      Create free account
    </Link>
  );

  return (
    <div data-skin="classic-red" className="min-h-screen bg-[#060607] text-[var(--text-primary)]" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[var(--border-subtle)] bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center px-5 py-3 sm:px-8">
          <Link to="/" data-testid="landing-brand"><Wordmark sizeClass="text-2xl sm:text-3xl" /></Link>
          <div className="ml-auto flex items-center gap-3">
            <Link to="/login" data-testid="landing-signin-link" className="text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Sign in</Link>
            <Link to="/register" data-testid="landing-register-top" className="rounded-full bg-[var(--accent)] px-4 py-2 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]">Create account</Link>
          </div>
        </div>
      </header>

      {/* Hero — full-bleed gear-dial logo background */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="hero-dial-spin absolute inset-0 bg-center bg-cover" style={{ backgroundImage: "url(/brand/the-dial-logo-v3.jpg)", filter: "brightness(0.3)" }} aria-hidden="true" />
        <div className="hero-dial-glow absolute inset-0" style={{ background: "radial-gradient(60% 50% at 50% 42%, rgba(224,27,27,0.22), transparent 70%)" }} aria-hidden="true" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(6,6,7,0.82) 0%, rgba(6,6,7,0.35) 42%, rgba(6,6,7,0.92) 100%)" }} aria-hidden="true" />
        <div className="relative mx-auto flex max-w-4xl flex-col items-center px-5 py-24 text-center sm:py-32">
          <p className="fade-up text-[11px] font-bold uppercase tracking-[0.34em] text-[#e01b1b] sm:text-xs">The Streaming Guide</p>
          <h1 className="fade-up mt-4 font-head text-6xl font-bold uppercase leading-[0.92] tracking-[0.04em] sm:text-7xl lg:text-8xl">
            <span className="text-white">THE </span>
            <span className="text-[#e01b1b]" style={{ textShadow: "0 0 32px rgba(224,27,27,0.55)" }}>DIAL</span>
          </h1>
          <p className="fade-up mt-6 max-w-lg text-base text-[var(--text-secondary)] sm:text-lg">
            so movie night doesn't turn into <span className="text-white underline decoration-[#e01b1b] decoration-2 underline-offset-4">find-a-movie night</span>.
          </p>
          <div className="fade-up mt-9 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row sm:gap-4">
            <Link to="/register" data-testid="hero-cta-register"
              className="inline-flex w-full items-center justify-center rounded-md bg-[#e01b1b] px-8 py-3.5 font-bold uppercase tracking-[0.12em] text-white transition-all hover:bg-[#ff2a2a] sm:w-auto"
              style={{ boxShadow: "0 0 28px rgba(224,27,27,0.45)" }}>
              Get Full Access
            </Link>
            <button onClick={() => goDemo(demoSkin)} data-testid="hero-cta-browse"
              className="inline-flex w-full items-center justify-center rounded-md border border-[#e01b1b]/70 px-8 py-3.5 font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#e01b1b]/10 sm:w-auto">
              Browse The Eras
            </button>
          </div>
          {/* tick-mark divider */}
          <div className="fade-up mt-12 h-4 w-full max-w-md" data-testid="hero-tick-divider" aria-hidden="true"
            style={{ background: "repeating-linear-gradient(90deg, #e01b1b 0 2px, transparent 2px 12px)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 18%, #000 82%, transparent)", maskImage: "linear-gradient(90deg, transparent, #000 18%, #000 82%, transparent)" }} />
        </div>
      </section>

      {/* Playable demo */}
      <Section className="pt-6 pb-10">
        <div ref={demoRef} className="mx-auto max-w-2xl scroll-mt-24" data-testid="hero-demo">
          <MockBrowse skinId={demoSkin} items={items} loading={loading} />
          <p className="mt-4 text-center text-xs font-bold uppercase tracking-[0.28em] text-[#e01b1b]">Try the eras — no account needed</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2" data-testid="era-buttons">
            {SKIN_TEASERS.map((s) => {
              const active = demoSkin === s.id;
              return (
                <button key={s.id} onClick={() => setDemoSkin(s.id)} data-testid={`era-button-${s.id}`}
                  className={`rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${active ? "border-[#e01b1b] bg-[#e01b1b] text-white" : "border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[#e01b1b] hover:text-white"}`}>
                  {s.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-center text-[11px] text-[var(--text-muted)]">Preview only — 2 eras are free, the other 6 need Full Access.</p>
        </div>
      </Section>

      {/* The pain */}
      <Section className="py-16">
        <div className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-8 sm:p-12">
          <h2 className="font-head text-3xl tracking-wide sm:text-4xl">You pay for 4 services and still scroll for 30 minutes.</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {[
              { icon: <Tv size={22} />, t: "No more app-hopping", d: "Everything in one place instead of bouncing between four apps." },
              { icon: <Search size={22} />, t: "One search, all of it", d: "Search once across every subscription you pay for." },
              { icon: <Check size={22} />, t: "Never lose the show", d: "Never forget which service has the show you actually want." },
            ].map((b, i) => (
              <div key={i} className="flex flex-col gap-2" data-testid={`pain-bullet-${i}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent-hover)]">{b.icon}</span>
                <p className="font-head text-lg text-[var(--text-primary)]">{b.t}</p>
                <p className="text-sm text-[var(--text-secondary)]">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* How it works */}
      <Section className="py-16">
        <h2 className="font-head text-3xl tracking-wide sm:text-4xl">How it works</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {[
            { n: "1", t: "Pick your services", d: "Tell The Dial which subscriptions you pay for." },
            { n: "2", t: "Browse everything together", d: "One guide across all of them, in the era style you choose." },
            { n: "3", t: "Tap through to watch", d: "Jump straight to the right app to press play." },
          ].map((s) => (
            <div key={s.n} className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6" data-testid={`step-${s.n}`}>
              <span className="font-head text-4xl text-[var(--accent)]">{s.n}</span>
              <p className="mt-3 font-head text-xl text-[var(--text-primary)]">{s.t}</p>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">{s.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 flex items-center gap-2 text-sm text-[var(--text-muted)]">
          <MousePointerClick size={16} className="text-[var(--accent-hover)]" />
          The Dial is the guide, not the channel — you always press play in the service's own app.
        </p>
      </Section>

      {/* The eras */}
      <Section className="py-16">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-head text-sm font-bold uppercase tracking-[0.3em] text-[#8a8a8e]">Pick Your Era</h2>
          <span className="border border-[#e01b1b]/50 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#e01b1b]">2 free forever — 6 more with Full Access</span>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          {SKIN_TEASERS.map((s) => {
            const free = FREE_SKINS.includes(s.id);
            const active = demoSkin === s.id;
            return (
              <button key={s.id} onClick={() => goDemo(s.id)} data-testid={`era-card-${s.id}`}
                className={`group relative overflow-hidden border bg-[#0b0b0d] p-5 text-left transition-all ${active ? "border-[#e01b1b]" : "border-[#1f1f22] hover:border-[#e01b1b]"}`}>
                <div className="mb-4 h-16 w-full rounded-sm" style={{ backgroundImage: s.swatch }} />
                <div className="h-px w-full bg-[#e01b1b]" />
                <div className="mt-3 flex items-center gap-2">
                  <p className="font-head text-lg font-bold uppercase tracking-wide text-white">{s.label}</p>
                  {free ? (
                    <span className="rounded bg-green-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-green-400">Free</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded bg-[#8a8a8e]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#8a8a8e]"><Lock size={9} /> Full Access</span>
                  )}
                </div>
                <p className="mt-0.5 text-[11px] uppercase tracking-wider text-[var(--text-muted)]">{s.era}</p>
                <p className="mt-2 text-sm text-[var(--text-secondary)]">{ERA_FLAVOR[s.id]}</p>
                <span className="mt-3 inline-block text-xs font-semibold text-[var(--accent-hover)] group-hover:underline">Preview this era ↑</span>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Indie Spotlight */}
      <Section className="py-16">
        <div className="rounded-[var(--card-radius)] border border-[var(--border-accent)] bg-[var(--accent)]/5 p-8 sm:p-12">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent-hover)]"><Film size={22} /></span>
          <h2 className="mt-4 font-head text-3xl tracking-wide sm:text-4xl">Hollywood isn't the only one making movies.</h2>
          <p className="mt-4 max-w-2xl text-base text-[var(--text-secondary)]">
            The Dial spotlights independent films from working filmmakers — titles you'd never stumble on scrolling the big services. It's a small, curated corner for the movies that deserve a bigger audience, and it lives right inside your guide.
          </p>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-16">
        <h2 className="font-head text-3xl tracking-wide sm:text-4xl">Questions, answered</h2>
        <div className="mt-8 divide-y divide-[var(--border-subtle)] rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)]">
          {FAQS.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={i} data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(open ? -1 : i)} data-testid={`faq-toggle-${i}`}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left">
                  <span className="font-head text-lg text-[var(--text-primary)]">{f.q}</span>
                  <ChevronDown size={18} className={`flex-none text-[var(--accent-hover)] transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && <p className="px-6 pb-5 text-sm text-[var(--text-secondary)]" data-testid={`faq-answer-${i}`}>{f.a}</p>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="py-20">
        <div className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-gradient-to-br from-[var(--bg-surface)] to-[var(--bg-card)] p-10 text-center sm:p-16">
          <h2 className="mx-auto max-w-3xl font-head text-3xl leading-tight tracking-wide sm:text-5xl">{HEADLINE}</h2>
          <div className="mt-8"><CtaPrimary testid="final-cta-register" /></div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-[var(--border-subtle)]" data-testid="landing-footer">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-4 px-5 py-10 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div>
            <Wordmark sizeClass="text-xl" />
            <p className="mt-2 text-xs text-[var(--text-muted)]">© 2026 The Dial · <a href="mailto:hello@thedialstream.com" className="hover:text-[var(--text-secondary)]" data-testid="footer-contact">hello@thedialstream.com</a></p>
            <p className="mt-2 max-w-md text-[11px] leading-relaxed text-[var(--text-muted)]" data-testid="tmdb-attribution">This product uses the TMDB API but is not endorsed or certified by TMDB.</p>
            <p className="mt-2 max-w-md text-[11px] leading-relaxed text-[var(--text-muted)]" data-testid="disclaimer-trademarks">All streaming service names and logos are trademarks of their respective owners. The Dial is not affiliated with or endorsed by any streaming service.</p>
          </div>
          <div className="flex items-center gap-5 text-sm text-[var(--text-secondary)]">
            <Link to="/faq" className="hover:text-[var(--text-primary)]" data-testid="footer-faq">FAQ</Link>
            <Link to="/login" className="hover:text-[var(--text-primary)]" data-testid="footer-signin">Sign in</Link>
            <Link to="/register" className="hover:text-[var(--text-primary)]" data-testid="footer-register">Create account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
